import React from 'react';
import BaseDrawer from './BaseDrawer';
import { SegmentData } from '../../contexts/AdminDataContext';
import { Users, TrendingUp, DollarSign, Activity } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
interface SegmentDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  segment: SegmentData | null;
}
const SegmentDetailDrawer: React.FC<SegmentDetailDrawerProps> = ({
  isOpen,
  onClose,
  segment
}) => {
  if (!segment) return null;
  // Generate mini chart data for the segment
  const chartData = Array.from({
    length: 7
  }, (_, i) => ({
    day: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'][i],
    value: segment.users * 0.12 + Math.random() * segment.users * 0.05
  }));
  return <BaseDrawer isOpen={isOpen} onClose={onClose} title={`Dettagli Segmento: ${segment.name}`} description="Analisi dettagliata del segmento" footer={<button onClick={onClose} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" data-id="element-810">
          Chiudi
        </button>} data-id="element-809">
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-4" data-id="element-811">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200" data-id="element-812">
          <div className="flex items-center gap-2 mb-2" data-id="element-813">
            <Users className="h-4 w-4 text-blue-600" data-id="element-814" />
            <p className="text-xs font-medium text-blue-700" data-id="element-815">Utenti</p>
          </div>
          <p className="text-2xl font-bold text-blue-900" data-id="element-816">
            {segment.users.toLocaleString()}
          </p>
        </div>

        <div className="bg-green-50 rounded-lg p-4 border border-green-200" data-id="element-817">
          <div className="flex items-center gap-2 mb-2" data-id="element-818">
            <Activity className="h-4 w-4 text-green-600" data-id="element-819" />
            <p className="text-xs font-medium text-green-700" data-id="element-820">Engagement</p>
          </div>
          <p className="text-2xl font-bold text-green-900" data-id="element-821">
            {segment.engagement}%
          </p>
        </div>

        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200" data-id="element-822">
          <div className="flex items-center gap-2 mb-2" data-id="element-823">
            <DollarSign className="h-4 w-4 text-purple-600" data-id="element-824" />
            <p className="text-xs font-medium text-purple-700" data-id="element-825">Revenue</p>
          </div>
          <p className="text-2xl font-bold text-purple-900" data-id="element-826">
            €{segment.revenue.toLocaleString()}
          </p>
        </div>

        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200" data-id="element-827">
          <div className="flex items-center gap-2 mb-2" data-id="element-828">
            <TrendingUp className="h-4 w-4 text-amber-600" data-id="element-829" />
            <p className="text-xs font-medium text-amber-700" data-id="element-830">Trend</p>
          </div>
          <p className="text-2xl font-bold text-amber-900" data-id="element-831">+{segment.trend}%</p>
        </div>
      </div>

      {/* Mini Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-832">
        <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-833">
          Attività Ultimi 7 Giorni
        </h4>
        <div className="h-48" data-id="element-834">
          <ResponsiveContainer width="100%" height="100%" data-id="element-835">
            <LineChart data={chartData} data-id="element-836">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" data-id="element-837" />
              <XAxis dataKey="day" stroke="#6B7280" fontSize={12} data-id="element-838" />
              <YAxis stroke="#6B7280" fontSize={12} data-id="element-839" />
              <Tooltip contentStyle={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '0.5rem'
            }} data-id="element-840" />
              <Line type="monotone" dataKey="value" stroke="#108d3e" strokeWidth={2} dot={{
              fill: '#108d3e',
              r: 4
            }} data-id="element-841" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Additional Info */}
      <div className="bg-gray-50 rounded-lg p-4" data-id="element-842">
        <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-843">
          Informazioni Aggiuntive
        </h4>
        <div className="space-y-2 text-sm" data-id="element-844">
          <div className="flex justify-between" data-id="element-845">
            <span className="text-gray-600" data-id="element-846">Revenue per Utente:</span>
            <span className="font-medium text-gray-900" data-id="element-847">
              €{(segment.revenue / segment.users).toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between" data-id="element-848">
            <span className="text-gray-600" data-id="element-849">Quota Utenti:</span>
            <span className="font-medium text-gray-900" data-id="element-850">
              {(segment.users / 4235 * 100).toFixed(1)}%
            </span>
          </div>
          <div className="flex justify-between" data-id="element-851">
            <span className="text-gray-600" data-id="element-852">Performance:</span>
            <span className={`font-medium ${segment.engagement > 70 ? 'text-green-600' : segment.engagement > 60 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-853">
              {segment.engagement > 70 ? 'Eccellente' : segment.engagement > 60 ? 'Buona' : 'Da migliorare'}
            </span>
          </div>
        </div>
      </div>
    </BaseDrawer>;
};
export default SegmentDetailDrawer;